import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import * as echarts from 'echarts'
import { IWorkDay } from '../../util/algorithm/algoInterface'

interface DayData {
  [day: string]: IWorkDay
}

interface Props {
  title: string
  data: any
  width?: string
  height?: string
  showLegend?: boolean
}

const COLORS = ['#d9e5ff', '#b2ccff', '#4374d9']

const Wrapper = styled.div<{ height?: string; width?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* 위쪽 정렬로 변경하여 높이가 줄어도 상단에 붙게 함 */
  align-items: center;
  height: ${(props) => props.height || 'auto'};
  width: ${(props) => props.width || '100%'};
  position: relative;
`

// [수정] 높이를 70%로 고정 (30% 축소)하고, showLegend 여부와 상관없이 동일하게 유지
const ChartContainer = styled.div`
  height: 70%;
  width: 100%;
`

const LegendWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
  height: 25px;
  margin-top: 5px; /* 차트와의 간격 */
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ColorBox = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  background-color: ${(props) => props.color};
  border-radius: 2px;
`

const LegendText = styled.span`
  font-family: SUIT;
  font-size: 10px;
  font-weight: 400;
  color: #666;
`

function convertDataToHeatmapFormat(data: DayData) {
  const result: Array<[number, number, number]> = []
  if (!data) return []

  Object.entries(data).forEach(([day, values]) => {
    Object.entries(values).forEach(([hour, value]) => {
      const dayIndex = getDayIndex(day)
      let valueIndex = -1
      if (value === 'min') valueIndex = 0
      if (value === 'mid') valueIndex = 1
      if (value === 'max') valueIndex = 2

      if (dayIndex !== -1 && valueIndex !== -1) {
        result.push([parseInt(hour), dayIndex, valueIndex])
      }
    })
  })
  return result
}

function getDayIndex(day: string) {
  switch (day) {
    case 'week':
      return 2
    case 'sat':
      return 1
    case 'holi':
      return 0
    default:
      return -1
  }
}

const TOUHeatMap = (props: Props) => {
  const { title, data, width, height, showLegend = false } = props
  const chartRef = useRef<echarts.ECharts | null>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const chartDom = chartContainerRef.current
    if (chartDom) {
      if (chartRef.current) chartRef.current.dispose()

      const chart = echarts.init(chartDom)
      const heatmapData = convertDataToHeatmapFormat(data)

      const option: echarts.EChartsOption = {
        title: {
          text: title,
          left: 'center',
          top: 0,
          textStyle: {
            fontSize: 12,
            fontFamily: 'SUIT',
            fontWeight: 'normal',
          },
        },
        tooltip: {
          position: 'top',
          confine: true,
        },
        grid: {
          top: '25px', // 타이틀 공간 확보
          bottom: '0px', // 바닥 여백 없앰 (컨테이너 자체가 70%라 충분)
          left: '5px',
          right: '10px',
          containLabel: true,
        },
        visualMap: {
          show: false,
          min: 0,
          max: 2,
          inRange: { color: COLORS },
        },
        xAxis: {
          type: 'category',
          // [수정] 호환성 좋은 방식 유지
          data: Array.from({ length: 24 }, (_, i) => String(i)),
          splitArea: { show: true },
          axisLabel: {
            fontSize: 9,
            interval: 0,
            margin: 2,
          },
          axisTick: { show: false },
          axisLine: { show: false },
        },
        yAxis: {
          type: 'category',
          data: ['공휴일', '토요일', '평일'],
          splitArea: { show: true },
          axisLabel: {
            fontSize: 10,
            margin: 5,
          },
          axisTick: { show: false },
          axisLine: { show: false },
        },
        series: [
          {
            name: 'TOU',
            type: 'heatmap',
            data: heatmapData,
            label: { show: false },
            itemStyle: {
              borderWidth: 1,
              borderColor: '#FFFFFF',
            },
          },
        ],
      }

      chart.setOption(option)
      chartRef.current = chart

      const handleResize = () => chart.resize()
      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
        if (chartRef.current) chartRef.current.dispose()
      }
    }
  }, [data, title, showLegend])

  return (
    <Wrapper width={width} height={height}>
      {/* [수정] showLegend prop을 전달하지 않고 항상 고정된 높이(70%)를 사용합니다.
        이렇게 하면 범례가 있든 없든 차트 크기는 항상 동일하게 유지됩니다.
      */}
      <ChartContainer ref={chartContainerRef} />

      {/* 범례는 차트 아래에 별도로 렌더링됩니다. */}
      {showLegend && (
        <LegendWrapper>
          <LegendItem>
            <ColorBox color={COLORS[0]} />
            <LegendText>경부하</LegendText>
          </LegendItem>
          <LegendItem>
            <ColorBox color={COLORS[1]} />
            <LegendText>중간부하</LegendText>
          </LegendItem>
          <LegendItem>
            <ColorBox color={COLORS[2]} />
            <LegendText>최대부하</LegendText>
          </LegendItem>
        </LegendWrapper>
      )}
    </Wrapper>
  )
}

export default TOUHeatMap
